// src/components/valuepyramid.js
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios8300 from '../services/axios8300';
import './valuepyramid.css';

const ValuePyramid = () => {
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const account_id = params.get('account_id');
    const user_id = params.get('user_id');
    const api_key = params.get('api_key');
    const solution_types = params.get('solution_types');
    const vendor_names = params.get('vendor_names');

    const [layers, setLayers] = useState({
        topLayer: { vision: '', goals: '', enterprise_objectives: '' },
        secondLayer: { business_priorities: '', competitors: '', partners: '', tech_stack: '' },
        thirdLayer: { account_status: '', current_spend: '', current_use_cases: '', recent_changes: '' },
        fourthLayer: { business_objectives: '', business_challenges: '', business_use_cases: '', technical_challenges: '' },
        fifthLayer: { focus_area: '', opportunities: '', customer_business_situation: '' }
    });

    const [loading, setLoading] = useState(false); // New state to track loading
    
    // Define handleInputChange
    const handleInputChange = (layerName, field, value) => {
        setLayers(prevLayers => ({
            ...prevLayers,
            [layerName]: {
                ...prevLayers[layerName],
                [field]: value
            }
        }));
    };

    useEffect(() => {
        const fetchAndBuildValuePyramid = async () => {
            setLoading(true); // Set loading to true when fetching starts
            try {
                const response = await axios8300.get('/fetch_account_details', {
                    params: { account_id, user_id, api_key }
                });

                if (response.data) {
                    const { customer_name, industry } = response.data;

                    if (!customer_name || !industry) {
                        console.error('Customer name or industry is missing. Cannot proceed.');
                        setLoading(false);
                        return;
                    }

                    const vendorNamesArray = vendor_names ? vendor_names.split(',').map(vendor => vendor.trim()) : [];
                    const solutionTypeString = solution_types ? solution_types.split(',').map(type => type.trim()).join(', ') : '';

                    const buildValuePyramidResponse = await axios8300.post('/build-value-pyramid/', {
                        user_id,
                        api_key,
                        account_id,
                        customer_name,
                        industry,
                        solution_type: solutionTypeString,
                        vendor_names: vendorNamesArray
                    });

                    if (buildValuePyramidResponse.data) {
                        setLayers(prevLayers => ({
                            topLayer: { ...prevLayers.topLayer, ...buildValuePyramidResponse.data.top_layer },
                            secondLayer: { ...prevLayers.secondLayer, ...buildValuePyramidResponse.data.second_layer },
                            thirdLayer: { ...prevLayers.thirdLayer, ...buildValuePyramidResponse.data.third_layer },
                            fourthLayer: { ...prevLayers.fourthLayer, ...buildValuePyramidResponse.data.fourth_layer },
                            fifthLayer: { ...prevLayers.fifthLayer, ...buildValuePyramidResponse.data.fifth_layer }
                        }));
                    }

                    const lastUpdatesResponse = await axios8300.get('/fetch_last_updates', {
                        params: { account_id, user_id, api_key }
                    });

                    if (lastUpdatesResponse.data) {
                        setLayers(prevLayers => ({
                            ...prevLayers,
                            ...lastUpdatesResponse.data
                        }));
                    } else {
                        console.warn('No last updates found for the account.');
                    }
                }
            } catch (error) {
                console.error('Error fetching account details or building value pyramid:', error.response ? error.response.data : error.message);
            } finally {
                setLoading(false); // Set loading to false when fetching completes
            }
        };

        fetchAndBuildValuePyramid();
    }, [account_id, user_id, api_key, solution_types, vendor_names]);

    const handleSaveLayer = async (layerName) => {
        setLoading(true); // Set loading to true when saving starts
        try {
            const dataToSave = layers[layerName];
            await axios8300.post('/save_layer', {
                user_id,
                api_key,
                account_id,
                layer: layerName,
                data: dataToSave
            });
            alert('Layer saved successfully');
        } catch (error) {
            console.error(`Error saving ${layerName}:`, error);
            alert(`Failed to save ${layerName}`);
        } finally {
            setLoading(false); // Set loading to false when saving completes
        }
    };

    const handleSaveAndExport = async () => {
        setLoading(true); // Set loading to true when exporting starts
        try {
            await axios8300.post('/save_and_export_value_pyramid', {
                user_id,
                api_key,
                account_id,
                value_pyramid: layers
            });
            alert('Value Pyramid saved and exported successfully');
        } catch (error) {
            console.error('Error saving and exporting Value Pyramid:', error);
            alert('Failed to save and export Value Pyramid');
        } finally {
            setLoading(false); // Set loading to false when exporting completes
        }
    };

    const formatContent = (content) => {
        try {
            // Check if content is an object or array, parse if necessary
            const parsedContent = typeof content === 'string' ? JSON.parse(content) : content;
    
            if (typeof parsedContent === 'object' && !Array.isArray(parsedContent)) {
                // Handle object - format key-value pairs
                return Object.entries(parsedContent).map(([key, value], i) => (
                    <p key={i}>
                        <strong>{key}:</strong> {value}
                    </p>
                ));
            } else if (Array.isArray(parsedContent)) {
                // Handle array - list items
                return parsedContent.map((item, i) => <p key={i}>{item}</p>);
            }
        } catch {
            // Fallback: Split by new lines for plain text
            return content.split("\n").map((paragraph, i) => <p key={i}>{paragraph.trim()}</p>);
        }
    };
    


return (
    <div className="scroll-container">
        {loading && <div className="loading-message">Processing...</div>}
        <h1>Value Pyramid</h1>

        <h2>Top Layer</h2>
        <div className="pyramid-layer">
            <label>Vision:</label>
            <div className="formatted-content">{formatContent(layers.topLayer.vision || '')}</div>
            <textarea
                value={layers.topLayer.vision || ''}
                onChange={(e) => handleInputChange('topLayer', 'vision', e.target.value)}
            />
            <label>Goals:</label>
            <div className="formatted-content">{formatContent(layers.topLayer.goals || '')}</div>
            <textarea
                value={layers.topLayer.goals || ''}
                onChange={(e) => handleInputChange('topLayer', 'goals', e.target.value)}
            />
            <label>Enterprise Objectives:</label>
            <div className="formatted-content">{formatContent(layers.topLayer.enterprise_objectives || '')}</div>
            <textarea
                value={layers.topLayer.enterprise_objectives || ''}
                onChange={(e) => handleInputChange('topLayer', 'enterprise_objectives', e.target.value)}
            />
            <button onClick={() => handleSaveLayer('topLayer')}>Save Top Layer</button>
        </div>

        <h2>Second Layer</h2>
<div className="pyramid-layer">
    <label>Business Priorities:</label>
    <div className="formatted-content">{formatContent(layers.secondLayer.business_priorities || '')}</div>
    <textarea
        value={layers.secondLayer.business_priorities || ''}
        onChange={(e) => handleInputChange('secondLayer', 'business_priorities', e.target.value)}
    />
    <label>Competitors:</label>
    <div className="formatted-content">{formatContent(layers.secondLayer.competitors || '')}</div>
    <textarea
        value={layers.secondLayer.competitors || ''}
        onChange={(e) => handleInputChange('secondLayer', 'competitors', e.target.value)}
    />
    <label>Partners:</label>
    <div className="formatted-content">{formatContent(layers.secondLayer.partners || '')}</div>
    <textarea
        value={layers.secondLayer.partners || ''}
        onChange={(e) => handleInputChange('secondLayer', 'partners', e.target.value)}
    />
    <label>Tech Stack:</label>
    <div className="formatted-content">{formatContent(layers.secondLayer.tech_stack || '')}</div>
    <textarea
        value={layers.secondLayer.tech_stack || ''}
        onChange={(e) => handleInputChange('secondLayer', 'tech_stack', e.target.value)}
    />
    <button onClick={() => handleSaveLayer('secondLayer')}>Save Second Layer</button>
</div>

<h2>Third Layer</h2>
<div className="pyramid-layer">
    <label>Account Status:</label>
    <div className="formatted-content">{formatContent(layers.thirdLayer.account_status || '')}</div>
    <textarea
        value={layers.thirdLayer.account_status || ''}
        onChange={(e) => handleInputChange('thirdLayer', 'account_status', e.target.value)}
    />
    <label>Current Spend:</label>
    <div className="formatted-content">{formatContent(layers.thirdLayer.current_spend || '')}</div>
    <textarea
        value={layers.thirdLayer.current_spend || ''}
        onChange={(e) => handleInputChange('thirdLayer', 'current_spend', e.target.value)}
    />
    <label>Current Use Cases:</label>
    <div className="formatted-content">{formatContent(layers.thirdLayer.current_use_cases || '')}</div>
    <textarea
        value={layers.thirdLayer.current_use_cases || ''}
        onChange={(e) => handleInputChange('thirdLayer', 'current_use_cases', e.target.value)}
    />
    <label>Recent Changes:</label>
    <div className="formatted-content">{formatContent(layers.thirdLayer.recent_changes || '')}</div>
    <textarea
        value={layers.thirdLayer.recent_changes || ''}
        onChange={(e) => handleInputChange('thirdLayer', 'recent_changes', e.target.value)}
    />
    <button onClick={() => handleSaveLayer('thirdLayer')}>Save Third Layer</button>
</div>

<h2>Fourth Layer</h2>
<div className="pyramid-layer">
    <label>Business Objectives:</label>
    <div className="formatted-content">{formatContent(layers.fourthLayer.business_objectives || '')}</div>
    <textarea
        value={layers.fourthLayer.business_objectives || ''}
        onChange={(e) => handleInputChange('fourthLayer', 'business_objectives', e.target.value)}
    />
    <label>Business Challenges:</label>
    <div className="formatted-content">{formatContent(layers.fourthLayer.business_challenges || '')}</div>
    <textarea
        value={layers.fourthLayer.business_challenges || ''}
        onChange={(e) => handleInputChange('fourthLayer', 'business_challenges', e.target.value)}
    />
    <label>Business Use Cases:</label>
    <div className="formatted-content">{formatContent(layers.fourthLayer.business_use_cases || '')}</div>
    <textarea
        value={layers.fourthLayer.business_use_cases || ''}
        onChange={(e) => handleInputChange('fourthLayer', 'business_use_cases', e.target.value)}
    />
    <label>Technical Challenges:</label>
    <div className="formatted-content">{formatContent(layers.fourthLayer.technical_challenges || '')}</div>
    <textarea
        value={layers.fourthLayer.technical_challenges || ''}
        onChange={(e) => handleInputChange('fourthLayer', 'technical_challenges', e.target.value)}
    />
    <button onClick={() => handleSaveLayer('fourthLayer')}>Save Fourth Layer</button>
</div>

<h2>Fifth Layer</h2>
<div className="pyramid-layer">
    <label>Focus Area:</label>
    <div className="formatted-content">{formatContent(layers.fifthLayer.focus_area || '')}</div>
    <textarea
        value={layers.fifthLayer.focus_area || ''}
        onChange={(e) => handleInputChange('fifthLayer', 'focus_area', e.target.value)}
    />
    <label>Opportunities:</label>
    <div className="formatted-content">{formatContent(layers.fifthLayer.opportunities || '')}</div>
    <textarea
        value={layers.fifthLayer.opportunities || ''}
        onChange={(e) => handleInputChange('fifthLayer', 'opportunities', e.target.value)}
    />
    <label>Customer Business Situation:</label>
    <div className="formatted-content">{formatContent(layers.fifthLayer.customer_business_situation || '')}</div>
    <textarea
        value={layers.fifthLayer.customer_business_situation || ''}
        onChange={(e) => handleInputChange('fifthLayer', 'customer_business_situation', e.target.value)}
    />
    <button onClick={() => handleSaveLayer('fifthLayer')}>Save Fifth Layer</button>
</div>

<button onClick={handleSaveAndExport}>Save and Export Value Pyramid</button>
</div>

);

};

export default ValuePyramid;
